import { useEsConfig } from "context/elasticsearch/esConfigContext";
//import { useNestedFieldSearch } from 'context/elasticsearch/nestedFields/useNestedFieldSearch'
import { CodeSystem } from "context/elasticsearch/types";
import {
  Button,
  ButtonGroup,
  ButtonToolbar,
  DropdownButton,
  Form,
  InputGroup
} from "react-bootstrap";

const EsConfigEditor: React.FC<{ codeSystem: CodeSystem }> = ({
  codeSystem
}) => {
  const {
    config,
    updateProperty,
    updateNestedProperty,
    revert,
    updateMinimumNestedFieldScore,
    updateMaximumNestedFieldValues,
    updateMinimumScore,
    updateDisplayScore
  } = useEsConfig(codeSystem);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateProp = (propName: string, updates: any) => {
    if (propName.includes(".") && propName.split(".")[1] !== "infix") {
      // parent name before the first dot, nested name after the first dot
      const parentName = propName.split(".")[0];
      const nestedName = propName.split(".").slice(1).join(".");
      updateNestedProperty(parentName, nestedName, updates);
    } else {
      updateProperty(propName, updates);
    }
  };

  return (
    <DropdownButton title="Søkevekting" variant="secondary">
      {[
        ...config.properties,
        ...(config.nestedProperties?.flatMap((nestedProp) => {
          const nestedPropertyName =
            nestedProp.displayName || nestedProp.propertyName;
          return nestedProp.nestedFields.map((field) => ({
            propertyName: `${nestedProp.propertyName}.${field.propertyName}`,
            displayName: `${nestedPropertyName} : ${
              field.displayName || field.propertyName
            }`,
            weight: field.weight,
            searchEnabled: field.searchEnabled,
            returnInResults: field.returnInResults
          }));
        }) || [])
      ].map((prop) => (
        <ButtonToolbar className="mb-1 ms-2 me-2" key={prop.propertyName}>
          <InputGroup className="" key={prop.propertyName}>
            <InputGroup.Checkbox
              aria-label="Checkbox for following text input"
              checked={prop.searchEnabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                updateProp(prop.propertyName, {
                  searchEnabled: e.target.checked
                });
              }}
            />
            <InputGroup.Text id="btnGroupAddon" style={{ width: "200px" }}>
              {prop.displayName || prop.propertyName}
            </InputGroup.Text>
          </InputGroup>
          <ButtonGroup className="" aria-label="First group">
            <Button
              variant="secondary"
              onClick={() => {
                updateProp(prop.propertyName, {
                  weight: prop.weight - 1
                });
              }}
            >
              -
            </Button>

            <Form.Control
              type="text"
              placeholder="w"
              aria-label="weight"
              aria-describedby="btnGroupAddon2"
              style={{ width: "50px" }}
              value={prop.weight}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateProp(prop.propertyName, {
                  weight: Number.parseFloat(e.target.value)
                })
              }
            />
            <Button
              variant="secondary"
              onClick={() =>
                updateProp(prop.propertyName, {
                  weight: prop.weight + 1
                })
              }
            >
              +
            </Button>
          </ButtonGroup>
        </ButtonToolbar>
      ))}
      <ButtonToolbar className="mb-1 ms-2 me-2" key={"minimumScore"}>
        <InputGroup className="" key={"minimumScore"}>
          <InputGroup.Checkbox
            aria-label="Checkbox for minimum score"
            checked={true}
            readOnly
          />
          <InputGroup.Text id="btnGroupAddon" style={{ width: "200px" }}>
            Minimum score
          </InputGroup.Text>
        </InputGroup>
        <ButtonGroup className="" aria-label="First group">
          <Button
            variant="secondary"
            onClick={() => {
              updateMinimumScore(config.minimumScore - 1);
            }}
          >
            -
          </Button>

          <Form.Control
            type="text"
            placeholder="w"
            aria-label="weight"
            aria-describedby="btnGroupAddon2"
            style={{ width: "60px" }}
            value={config.minimumScore}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateMinimumScore(Number.parseInt(e.target.value))
            }
          />
          <Button
            variant="secondary"
            onClick={() =>
              updateMinimumScore(config.minimumNestedFieldScore + 1)
            }
          >
            +
          </Button>
        </ButtonGroup>
      </ButtonToolbar>
      <ButtonToolbar className="mb-1 ms-2 me-2" key={"minimumNestedFieldScore"}>
        <InputGroup className="" key={"minimumNestedFieldScore"}>
          <InputGroup.Checkbox
            aria-label="Checkbox for minimum codes score"
            checked={true}
            readOnly
          />
          <InputGroup.Text id="btnGroupAddon" style={{ width: "200px" }}>
            {codeSystem == CodeSystem.Adm
              ? "Min. kode-score"
              : "Min. synonym-score"}
          </InputGroup.Text>
        </InputGroup>
        <ButtonGroup className="" aria-label="First group">
          <Button
            variant="secondary"
            onClick={() => {
              updateMinimumNestedFieldScore(config.minimumNestedFieldScore - 1);
            }}
          >
            -
          </Button>

          <Form.Control
            type="text"
            placeholder="w"
            aria-label="weight"
            aria-describedby="btnGroupAddon2"
            style={{ width: "45px" }}
            value={config.minimumNestedFieldScore}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateMinimumNestedFieldScore(Number.parseInt(e.target.value))
            }
          />
          <Button
            variant="secondary"
            onClick={() =>
              updateMinimumNestedFieldScore(config.minimumNestedFieldScore + 1)
            }
          >
            +
          </Button>
        </ButtonGroup>
      </ButtonToolbar>
      {codeSystem == CodeSystem.Adm && (
        <ButtonToolbar
          className="mb-1 ms-2 me-2"
          key={"maximumNestedFieldValues"}
        >
          <InputGroup className="" key={"maximumNestedFieldValues"}>
            <InputGroup.Checkbox
              aria-label="Checkbox for maximum nested field values"
              checked={true}
              readOnly
            />
            <InputGroup.Text id="btnGroupAddon" style={{ width: "200px" }}>
              Maks antall koder
            </InputGroup.Text>
          </InputGroup>
          <ButtonGroup className="" aria-label="First group">
            <Button
              variant="secondary"
              onClick={() => {
                updateMaximumNestedFieldValues(
                  config.maximumNestedFieldValues - 1
                );
              }}
            >
              -
            </Button>

            <Form.Control
              type="text"
              placeholder="w"
              aria-label="weight"
              aria-describedby="btnGroupAddon2"
              style={{ width: "45px" }}
              value={config.maximumNestedFieldValues}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateMaximumNestedFieldValues(Number.parseInt(e.target.value))
              }
            />
            <Button
              variant="secondary"
              onClick={() =>
                updateMaximumNestedFieldValues(
                  config.maximumNestedFieldValues + 1
                )
              }
            >
              +
            </Button>
          </ButtonGroup>
        </ButtonToolbar>
      )}

      <ButtonToolbar className="mb-1 ms-2 me-2" key={"displayScore"}>
        <InputGroup className="" key={"displayScore"}>
          <InputGroup.Checkbox
            aria-label="Checkbox for displaying search score"
            checked={config.displayScore}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateDisplayScore(e.target.checked)
            }
          />
          <InputGroup.Text id="btnGroupAddon" style={{ width: "200px" }}>
            Vis søke-score
          </InputGroup.Text>
        </InputGroup>
        <ButtonGroup className="" aria-label="First group"></ButtonGroup>
      </ButtonToolbar>
      <Button
        variant="primary"
        onClick={() => revert()}
        className="d-block w-50 mx-auto"
      >
        Nullstill
      </Button>
    </DropdownButton>
  );
};

export default EsConfigEditor;
